import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { optImg } from "@/lib/img";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/store/Reveal";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  const jsonLd = post ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.meta_description || post.excerpt,
        image: post.cover_image,
        articleSection: post.category,
        keywords: (post.keywords || []).join(", "),
        datePublished: post.created_at,
        author: { "@type": "Organization", name: "The Pet Shopping Co" },
        publisher: { "@type": "Organization", name: "The Pet Shopping Co" },
      },
      ...(post.faq && post.faq.length ? [{
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.question || f.q,
          acceptedAnswer: { "@type": "Answer", text: f.answer || f.a },
        })),
      }] : []),
    ],
  } : null;

  useSEO({
    title: post ? `${post.title} | The Pet Shopping Co` : "Artículo | The Pet Shopping Co",
    description: post?.meta_description || post?.excerpt,
    image: post?.cover_image,
    canonicalPath: `/blog/${slug}`,
    jsonLd,
  });

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <p className="font-display text-2xl font-bold text-charcoal">Artículo no encontrado</p>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-coral font-semibold"><ArrowLeft className="w-4 h-4" /> Volver al blog</Link>
      </div>
    );
  }

  if (!post) return <div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin text-coral" /></div>;

  const faq = (post.faq || []).map((f) => ({ q: f.question || f.q, a: f.answer || f.a })).filter((f) => f.q && f.a);

  return (
    <article className="pb-24">
      {/* hero */}
      <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <img src={optImg(post.cover_image, 1100)} alt={post.title} fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-charcoal/20" />
        <div className="relative z-10 h-full max-w-3xl mx-auto px-5 flex flex-col justify-end pb-10">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-cream/80 hover:text-cream mb-4"><ArrowLeft className="w-4 h-4" /> Blog</Link>
            <span className="text-xs font-semibold tracking-widest uppercase text-coral">{post.category}</span>
            <h1 className="font-display text-3xl sm:text-5xl font-bold text-cream tracking-tight mt-2 leading-[1.05]">{post.title}</h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 mt-10">
        {post.excerpt && <p className="text-lg text-charcoal/80 leading-relaxed border-l-4 border-coral pl-4 italic">{post.excerpt}</p>}

        <div className="prose-content mt-8">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {post.tags.map((t) => <span key={t} className="px-3 py-1 rounded-full bg-sage/50 text-charcoal text-xs font-semibold">#{t}</span>)}
          </div>
        )}

        {faq.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-5">Preguntas frecuentes</h2>
            <div className="space-y-3">
              {faq.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#E3E7E2] overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 p-5 text-left">
                    <span className="font-semibold text-charcoal">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-coral shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="px-5 pb-5 text-charcoal/75 leading-relaxed -mt-1">{f.a}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-14 rounded-3xl bg-peach/60 border border-[#E3E7E2] p-8 text-center">
          <h3 className="font-display text-xl font-bold text-charcoal">¿Buscas productos para tu mascota?</h3>
          <Link to="/tienda" className="mt-4 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-charcoal text-cream font-semibold hover:bg-coral transition-colors">Ver la tienda</Link>
        </div>
      </div>
    </article>
  );
}
